/**
 * フロントエンドログユーティリティ
 * ログレベルの管理とバックエンドへのログ送信機能を提供
 */

import axios from './axiosInterceptor';
import { LOG_API_URL } from '../config';
import { SessionManager } from './SessionManager';

// ログレベル
export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  FATAL: 'fatal'
} as const;

export type LogLevelType = typeof LogLevel[keyof typeof LogLevel];

// 開発環境かどうかの判定
const isDevelopment = process.env.NODE_ENV !== 'production';

// ローカルストレージにログを保存するキー
const LOG_STORAGE_KEY = 'app_logs';
const MAX_STORED_LOGS = 50;

// ログデータの型定義
export interface LogData {
  timestamp: string;
  level: LogLevelType;
  message: string;
  data?: any;
  userId?: number | null;
  email?: string | null;
}

// サーバー送信用ログデータの型定義
export interface ServerLogData extends LogData {
  userAgent: string;
  url: string;
}

// グローバルログイベント関数の型定義
declare global {
  interface Window {
    logClientEvent: (level: LogLevelType, data?: { message?: string; [key: string]: any }) => boolean;
  }
}

/**
 * 現在のユーザーIDを取得
 */
const getCurrentUserId = (): number | null => {
  try {
    const session = SessionManager.getActiveSession();
    return session ? session.userId : null;
  } catch (e) {
    console.error('Failed to get current user ID:', e);
    return null;
  }
};

/**
 * 現在のユーザーのメールアドレスを取得
 */
const getCurrentUserEmail = (): string | null => {
  try {
    const session = SessionManager.getActiveSession();
    return session ? session.email : null;
  } catch (e) {
    console.error('Failed to get current user email:', e);
    return null;
  }
};

/**
 * グローバルログイベント関数 - 全アプリからアクセス可能
 */
if (typeof window !== 'undefined') {
  // 再帰ループを防止するフラグ
  let isLogging = false;
  
  window.logClientEvent = (level: LogLevelType, data: { message?: string; [key: string]: any } = {}): boolean => {
    // 既にログ記録中なら何もしない（無限ループ防止）
    if (isLogging) return true;
    
    try {
      isLogging = true;
      const logLevel = Object.values(LogLevel).includes(level) ? level : LogLevel.INFO;
      const message = data.message || 'Client event logged';
      
      // コンソールには出力するが、再帰的なログ記録は行わない
      switch (logLevel) {
        case LogLevel.DEBUG:
          console.debug(`[DEBUG]: ${message}`, data);
          break;
        case LogLevel.INFO:
          console.info(`[INFO]: ${message}`, data);
          break;
        case LogLevel.WARN:
          console.warn(`[WARN]: ${message}`, data);
          break;
        case LogLevel.ERROR:
          console.error(`[ERROR]: ${message}`, data);
          break;
        case LogLevel.FATAL:
          console.error(`[FATAL]: ${message}`, data);
          break;
        default:
          console.info(`[INFO]: ${message}`, data);
      }
      
      // ローカルストレージにログを保存（例外発生時は抑制）
      try {
        const userId = getCurrentUserId();
        const email = getCurrentUserEmail();
        
        const log: LogData = {
          timestamp: new Date().toISOString(),
          level: logLevel,
          message,
          data,
          userId,
          email
        };

        const storedLogs = (() => {
          try {
            const stored = localStorage.getItem(LOG_STORAGE_KEY);
            return stored ? JSON.parse(stored) as LogData[] : [];
          } catch (e) {
            return [];
          }
        })();
        
        storedLogs.push(log);
        
        // 古いログを削除してストレージサイズを制限
        while (storedLogs.length > MAX_STORED_LOGS) {
          storedLogs.shift();
        }
        
        localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(storedLogs));
      } catch (e) {
        // ローカルストレージへの保存エラーは抑制
      }
      
      // 重要なエラーは直接サーバーに送信（logger.jsの他のメソッドは使わない）
      if (logLevel === LogLevel.ERROR || logLevel === LogLevel.FATAL) {
        try {
          const userId = getCurrentUserId();
          const email = getCurrentUserEmail();
          
          const logData: ServerLogData = {
            level: logLevel,
            message,
            data,
            userId,
            email,
            userAgent: window.navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString()
          };
          
          fetch(LOG_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(logData),
            keepalive: true
          }).catch(() => {
            // 送信エラーは抑制
          });
        } catch (e) {
          // エラー送信時のエラーも抑制
        }
      }
      
      return true;
    } catch (e) {
      // 全体的なエラーも抑制
      return false;
    } finally {
      isLogging = false;
    }
  };
}

/**
 * ログをローカルストレージに保存
 */
const storeLog = (level: LogLevelType, message: string, data: any): void => {
  try {
    const userId = getCurrentUserId();
    const email = getCurrentUserEmail();
    
    const log: LogData = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      userId,
      email
    };

    const storedLogs = logger.getLogs();
    storedLogs.push(log);
    
    // 古いログを削除してストレージサイズを制限
    while (storedLogs.length > MAX_STORED_LOGS) {
      storedLogs.shift();
    }
    
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(storedLogs));
  } catch (e) {
    console.error('Failed to store log in local storage:', e);
  }
};

/**
 * ログをバックエンドサーバーに送信
 */
const sendLogToServer = async (level: LogLevelType, message: string, data: any, sync = false): Promise<void> => {
  const userId = getCurrentUserId();
  const email = getCurrentUserEmail();
  
  const logData: ServerLogData = {
    level,
    message,
    data,
    userId,
    email,
    userAgent: window.navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString()
  };

  try {
    if (sync) {
      // 同期的に送信（致命的エラーの場合）
      await axios.post(LOG_API_URL, logData);
    } else {
      // 非同期で送信
      axios.post(LOG_API_URL, logData).catch(e => {
        console.error('Failed to send log to server:', e);
      });
    }
  } catch (error) {
    console.error('Error sending log to server:', error);
  }
};

/**
 * ログをコンソールに出力し、ストレージに保存
 * エラーログはバックエンドにも送信
 */
const logger = {
  /**
   * デバッグログ (開発環境でのみ出力)
   */
  debug: (message: string, data: any = {}): void => {
    if (isDevelopment) {
      console.debug(`[DEBUG]: ${message}`, data);
      storeLog(LogLevel.DEBUG, message, data);
    }
  },

  /**
   * 情報ログ
   */
  info: (message: string, data: any = {}): void => {
    console.info(`[INFO]: ${message}`, data);
    storeLog(LogLevel.INFO, message, data);
  },

  /**
   * 警告ログ
   */
  warn: (message: string, data: any = {}): void => {
    console.warn(`[WARN]: ${message}`, data);
    storeLog(LogLevel.WARN, message, data);
  },

  /**
   * エラーログ (バックエンドにも送信)
   */
  error: (message: string, error: any = null): void => {
    let errorData = error;
    
    // Error オブジェクトの場合は必要な情報を抽出
    if (error instanceof Error) {
      errorData = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        // エラーオブジェクトに追加プロパティがある場合抽出
        ...(typeof error === 'object' ? error : {})
      };
    }
    
    console.error(`[ERROR]: ${message}`, errorData);
    storeLog(LogLevel.ERROR, message, errorData);
    
    // エラーログをバックエンドに送信（非同期）
    try {
      sendLogToServer(LogLevel.ERROR, message, errorData);
    } catch (e) {
      console.error('Failed to send error log to server:', e);
    }
  },

  /**
   * 致命的エラーログ (アプリケーション全体に影響するエラー、バックエンドにも送信)
   */
  fatal: (message: string, error: any = null): void => {
    let errorData = error;
    
    if (error instanceof Error) {
      errorData = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(typeof error === 'object' ? error : {})
      };
    }
    
    console.error(`[FATAL]: ${message}`, errorData);
    storeLog(LogLevel.FATAL, message, errorData);
    
    // 致命的エラーログをバックエンドに送信（同期的）
    try {
      sendLogToServer(LogLevel.FATAL, message, errorData, true);
    } catch (e) {
      console.error('Failed to send fatal error log to server:', e);
    }
  },

  /**
   * 保存されたログを取得
   */
  getLogs: (): LogData[] => {
    try {
      const storedLogs = localStorage.getItem(LOG_STORAGE_KEY);
      return storedLogs ? JSON.parse(storedLogs) as LogData[] : [];
    } catch (e) {
      console.error('Failed to retrieve logs from storage:', e);
      return [];
    }
  },

  /**
   * 保存されたログをクリア
   */
  clearLogs: (): void => {
    try {
      localStorage.removeItem(LOG_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear logs from storage:', e);
    }
  },

  /**
   * 開発環境専用ログ（開発環境でのみ出力）
   * @param message ログメッセージ
   * @param data 追加データ（オプション）
   */
  devLog: (message: string, data?: unknown): void => {
    if (isDevelopment) {
      if (data !== undefined && data !== null) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }
};

export default logger;
